import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Eraser, Save, X } from 'lucide-react';

export default function SignaturePad({ onSave, onCancel }) {
  const sigCanvas = useRef(null);
  const containerRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [canvasSize, setCanvasSize] = useState({ width: null, height: null });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const w = Math.floor(rect.width);
        const h = Math.floor(rect.height);
        if (w > 0 && h > 0) setCanvasSize({ width: w, height: h });
      }
    };
    const timer = setTimeout(measure, 150);
    return () => clearTimeout(timer);
  }, []);

  const clear = () => {
    sigCanvas.current?.clear();
    setIsEmpty(true);
  };

  const handleEnd = () => {
    setIsEmpty(sigCanvas.current?.isEmpty() ?? true);
  };

  const save = () => {
    if (sigCanvas.current && !isEmpty) {
      try {
        const dataUrl = sigCanvas.current.toDataURL('image/png');
        onSave(dataUrl);
      } catch (e) {
        try {
          const canvas = sigCanvas.current.getCanvas();
          const dataUrl = canvas.toDataURL('image/png');
          onSave(dataUrl);
        } catch (e2) {
          console.error('署名の保存に失敗', e2);
        }
      }
    }
  };

  const content = (
    <div
      data-testid="signature-pad"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        touchAction: 'none',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fff',
        flexShrink: 0,
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>電子サイン</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="button" variant="outline" size="sm" onClick={clear} data-testid="signature-clear-button">
            <Eraser size={16} className="mr-1" />クリア
          </Button>
          {onCancel && (
            <Button type="button" variant="outline" size="sm" onClick={onCancel} data-testid="signature-cancel-button">
              <X size={16} className="mr-1" />閉じる
            </Button>
          )}
          <Button type="button" size="sm" onClick={save} disabled={isEmpty} data-testid="signature-save-button">
            <Save size={16} className="mr-1" />署名を保存
          </Button>
        </div>
      </div>

      <div ref={containerRef} style={{ flex: 1, position: 'relative' }}>
        {canvasSize.width ? (
          <SignatureCanvas
            ref={sigCanvas}
            penColor="#2A4F3D"
            minWidth={1.5}
            maxWidth={3}
            canvasProps={{
              width: canvasSize.width,
              height: canvasSize.height,
              className: 'signature-canvas',
              style: { width: '100%', height: '100%', display: 'block' }
            }}
            onEnd={handleEnd}
          />
        ) : (
          <div style={{ width: '100%', height: '100%' }} />
        )}
        {isEmpty && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <p style={{ color: '#9ca3af', fontSize: '20px' }}>ここに署名してください</p>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
